/**
 * Backend HTTP dependencies injected into the Http*Api classes so they are
 * decoupled from the SDK playground globals (getFirebaseClient/getCloudRunUrl).
 * Each host (PlaygroundHost, Telegram shell, future Steam shell) supplies its own.
 */
export interface BackendHttpDeps {
  getIdToken(): Promise<string>
  getCloudRunUrl(): string
  /**
   * Returns the host's game-session token (`x-h5-game-session`) for the current
   * game+user, or `null` when unavailable. Optional so non-playground shells
   * that don't mint a GST omit the header and stay fail-safe.
   */
  getGameSessionToken?(): Promise<string | null>
}

/**
 * Fail-safe resolver for an optional, possibly-rejecting `getGameSessionToken`.
 * Missing accessor → `null`; a rejection → `null`. Every Family-A client routes
 * its GST fetch through this so a GST hiccup never throws out of a backend call
 * (Req 7), regardless of which host supplied the dep.
 */
export async function resolveGameSessionToken(
  getGameSessionToken?: () => Promise<string | null>,
): Promise<string | null> {
  if (!getGameSessionToken) return null
  try {
    return await getGameSessionToken()
  } catch {
    return null
  }
}

/**
 * Authenticated fetch shared by the backend HTTP clients: resolves the
 * idToken and game-session token in parallel, attaches the auth headers,
 * and returns the raw `Response` so each client keeps its own error
 * mapping. `body` is JSON-stringified (dropping `undefined` optionals).
 */
export async function authedBackendFetch(
  deps: BackendHttpDeps,
  method: 'GET' | 'POST',
  path: string,
  body?: Record<string, unknown>,
): Promise<Response> {
  const [idToken, gst] = await Promise.all([
    deps.getIdToken(),
    resolveGameSessionToken(deps.getGameSessionToken),
  ])
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`,
  }
  if (gst) headers['x-h5-game-session'] = gst

  return fetch(`${deps.getCloudRunUrl()}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  })
}
